import { useState } from "react";
import { Link } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { Alert, AlertIcon } from "solveiq.designsystem";
import SignUpForm from "./SignUpForm";
import RESTGatewayAPI from "../../api/gatewayAPI";
import useQuery from "../../hooks/useQuery";
import SuccessAlert from "../../components/SuccessAlert/SuccessAlert";
import styles from "./SignUpScreen.module.css";
import { reportFullStoryEvent, useKissMetrics } from "ui.common";

interface ISignUpSubmitModel {
  name: string;
  email: string;
  password: string;
}

declare const agentUiURL: string;

function SignUpScreen() {
  const query = useQuery();
  const kissmetrics = useKissMetrics();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showExistModal, setShowExistModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const uuid = query.get("uuid");

  const handleSubmit = async ({
    email,
    name,
    password,
  }: ISignUpSubmitModel) => {
    // Verify if the user exists
    reportFullStoryEvent("Signup Form Submit", { email, name });
    kissmetrics.report("Signup Form Submit", []);
    const getExistAccountUrl = `/api/Checkout/existingaccount/${email}`;

    const responseExistAccount = await RESTGatewayAPI.get(getExistAccountUrl);
    // Should contain a property named AllowPurchase
    const shouldAllowCreateAccount =
      responseExistAccount?.data?.data?.AllowPurchase;

    //In case false the account already exists
    if (!shouldAllowCreateAccount) {
      reportFullStoryEvent("Signup Form Existing Account", { email, name });
      kissmetrics.report("Signup Form Existing Account", []);
      setShowExistModal(true);
      return;
    }

    //In case undefined something went wrong in the backend
    if (shouldAllowCreateAccount === undefined) {
      setShowErrorModal(true);
      return;
    }

    const signupUrl = `/api/Checkout/subscribe/postinstall`;

    const signupBody = {
      productID: 5,
      paymentInfo: {
        Name: name,
        Email: email,
      },
      seatCount: 20,
      queryStringData: new URLSearchParams(window.location.search).toString(),
      uuid,
      isBeta: true,
      password: password,
      sendCheckLicense: true,
      showUI: true,
    };

    const signupResponse = await RESTGatewayAPI.post(signupUrl, signupBody);

    if (signupResponse?.status === 200 && signupResponse?.data?.succeeded) {
      reportFullStoryEvent("Signup Form Success", { email, name });
      kissmetrics.report("Signup Form Success", []);
      setShowSuccessModal(true);
    } else {
      setShowErrorModal(true);
    }
  };

  const displayAccountAlreadyExistModal = () => (
    <Alert
      isOpen={showExistModal}
      icon={AlertIcon.Warning}
      approveHandler={() => {
        setShowExistModal(false);
        if (agentUiURL)
          window.open(agentUiURL, "_blank", "noopener,noreferrer");
      }}
      closeHandler={() => {
        setShowExistModal(false);
      }}
      title="Existing Account Detected"
      text="We've detected that you already have an account with Solve iQ Web."
      buttonText="Manage Account"
    />
  );
  const displayErrorModal = () => (
    <Alert
      isOpen={showErrorModal}
      icon={AlertIcon.Warning}
      approveHandler={() => {
        setShowErrorModal(false);
      }}
      closeHandler={() => {
        setShowExistModal(false);
      }}
      title="Something went wrong"
      text="Please retry later."
      buttonText="Close"
    />
  );
  const displaySuccessModal = () => (
    <SuccessAlert
      isOpen={showSuccessModal}
      title="Thanks for signing up!"
      text="Solve iQ Web should load momentarily."
    />
  );

  return (
    <div className={styles.screenContainer}>
      <section className={styles.card}>
        <header className={styles.cardHeader}>
          <h1 className={styles.title}>
            <FormattedMessage
              id="signup.screen.title"
              defaultMessage="Create Your Free Account"
            />
          </h1>
          <p className={styles.subtitle}>
            <FormattedMessage
              id="signup.screen.subTitle"
              defaultMessage="This will allow you to manage your services."
            />
          </p>
        </header>
        <div className={styles.formContainer}>
          <SignUpForm onSubmit={handleSubmit} />
        </div>
        <footer className={styles.cardFooter}>
          <Link
            className={styles.loginContainer}
            href={agentUiURL}
            variant="body1"
          >
            <FormattedMessage
              id="signup.screen.alreadyHaveAnAccount"
              defaultMessage="Already have an account?"
            >
              {(text) => <p className={styles.alreadyHaveAnAccount}>{text}</p>}
            </FormattedMessage>
            <FormattedMessage id="signup.screen.login" defaultMessage="Log in">
              {(text) => <span className={styles.loginText}>{text}</span>}
            </FormattedMessage>
          </Link>
        </footer>
      </section>
      {displayAccountAlreadyExistModal()}
      {displayErrorModal()}
      {displaySuccessModal()}
    </div>
  );
}

export default SignUpScreen;
